// Edit Pesanan PEMBELI (produk OPEN PO) — helper murni, tanpa React/Supabase.
//
// RPC `update_open_preorder_order_items` menulis ulang HANYA baris ber-
// `keep_status` NULL (baris batal & ber-keep dibiarkan, mig 5280 D4-7). Sejak
// mig 6460 pesanan ber-voucher ikut bisa diedit pembeli: sesudah baris ditulis
// ulang, server menghitung ulang voucher YANG SAMA dengan mesin checkout, atau
// menolak seluruh edit (pesanan tak berubah) bila vouchernya tak lagi berlaku.

import { MAX_LINE_NOTE_LENGTH, normalizeLineNote, type CartLine } from "@/lib/cartMulti";
import { newLineUid } from "@/lib/orderEditorLines";
import { resolveSelectionPrice, sellableSelectionPrice, type PriceTier } from "@/lib/pricing";
import type { ProductVariation } from "@/lib/products";
import type { PriceCut, ProductPromo } from "@/lib/promo";
import type { CustomerGroup } from "@/lib/reseller-groups";
import { discountTracePayload } from "@/lib/voucher";
import { orderLinePriceExtraKey } from "@/lib/orderLinePriceExtra";

/** Kolom `order_items` yang dibaca papan pesanan pembeli (`CUSTOMER_ORDERS_BOARD_SELECT`). */
export type OrderEditItemRow = {
  variant_id?: string | null;
  variant_name: string;
  unit_price?: number | null;
  qty: number;
  item_note?: string | null;
  keep_status?: string | null;
  voucher_name?: string | null;
  /** Peran hadiah pembelian (mig 8680) — baris `hadiah` tak ikut ditulis ulang (mig 8850). */
  gift_role?: string | null;
};

/**
 * Baris yang dimuat modal Edit Pesanan = baris yang AKAN ditulis ulang RPC.
 *
 * Dulu modal memuat SEMUA baris, termasuk yang sudah dibatalkan (Ubah Pcs /
 * Batalkan item admin memecah baris batal). Qty baris batal ikut terkirim, dan
 * RPC — yang hanya menghapus baris keep NULL — menyisipkannya sebagai pcs HIDUP:
 * pcs yang sudah batal hidup lagi dan ikut ditagih. Pada pesanan ber-voucher
 * (mig 6460) ia bahkan ikut memperoleh potongan voucher lagi.
 *
 * Baris HADIAH pembelian (mig 8850) juga tak dimuat: RPC tak menulisnya ulang —
 * hadiah ikut barang syaratnya dan digeser server sesudah Simpan.
 */
export function orderEditLinesFromItems(items: readonly OrderEditItemRow[]): CartLine[] {
  return items
    .filter((item) => item.keep_status == null && item.gift_role !== "hadiah")
    .map((item) => ({
      variationId: item.variant_id ?? item.variant_name,
      variationName: item.variant_name,
      unitPrice: item.unit_price ?? undefined,
      qty: item.qty,
      note: item.item_note ?? undefined
    }));
}

// ─── Editor: SATU BARIS = SATU (VARIAN + CATATAN) ────────────────────────────
//
// Satu varian bisa punya beberapa baris bercatatan beda ("Merah / L — Supi",
// "Merah / L — Rina": nama penerima per pcs, lahir dari checkout kedua yang
// melebur atau dari editor admin). Dulu editor ini mengunci baris pada id
// VARIAN saja: mengetik catatan satu baris menimpa kembarannya, ubah jumlah /
// Hapus ikut mengenai kembarannya, dan Simpan meleburnya jadi satu baris
// bercatatan TERAKHIR — catatan lain hilang permanen walau barisnya tak
// disentuh, karena RPC menulis ulang semua baris keep NULL. Aturan
// penggantinya sama dengan editor Detail PO admin (`orderEditorLines.ts`):
// identitas = varian + catatan, aksi = per baris.

/** Satu baris editor Edit Pesanan: `CartLine` + kunci baris yang stabil. */
export type OrderEditorLine = CartLine & {
  /** Kunci React & sasaran aksi — dua baris boleh ber-varian sama persis. */
  uid: string;
};

/**
 * Identitas baris = varian (id, atau nama bila tak ber-id — cermin RPC) +
 * catatan dalam bentuk yang DISIMPAN server (`nullif(left(trim(note), 20), '')`):
 * "  Supi " dan "Supi" satu identitas, "" sama dengan tanpa catatan. Pemisah
 * NUL supaya tak ada pemenggalan varian/catatan yang bertabrakan.
 */
export function orderEditLineIdentity(line: {
  variationId: string;
  variationName: string;
  note?: string | null;
}): string {
  const variant = line.variationId.trim() || line.variationName.trim();
  return `${variant}\u0000${normalizeLineNote(line.note ?? undefined) ?? ""}`;
}

/**
 * Baris editor dari baris pesanan (`orderEditLinesFromItems` / `order.lines`):
 * baris SEIDENTITAS digabung (qty dijumlah — kembar murni dari checkout yang
 * melebur), baris bercatatan beda tetap sendiri-sendiri. Urutan kemunculan
 * pertama dipertahankan.
 */
export function orderEditorLinesFrom(lines: readonly CartLine[]): OrderEditorLine[] {
  const out: OrderEditorLine[] = [];
  const byIdentity = new Map<string, OrderEditorLine>();
  for (const line of lines) {
    const identity = orderEditLineIdentity(line);
    const prev = byIdentity.get(identity);
    if (prev) {
      prev.qty += line.qty;
      if (prev.unitPrice === undefined) prev.unitPrice = line.unitPrice;
      continue;
    }
    const next: OrderEditorLine = {
      uid: newLineUid(),
      variationId: line.variationId,
      variationName: line.variationName,
      unitPrice: line.unitPrice,
      qty: line.qty,
      note: normalizeLineNote(line.note)
    };
    byIdentity.set(identity, next);
    out.push(next);
  }
  return out;
}

/** Qty satu varian di editor — SEMUA barisnya, termasuk kembaran bercatatan. */
export function orderEditorVariantQty(
  lines: readonly OrderEditorLine[],
  variationId: string | undefined
): number {
  if (!variationId) return 0;
  return lines.reduce((sum, l) => (l.variationId === variationId ? sum + l.qty : sum), 0);
}

/**
 * Tambah pcs varian dari tombol grup: masuk ke baris varian itu yang TANPA
 * catatan; bila semua barisnya bercatatan, jadi baris baru tanpa catatan —
 * pcs tambahan tak pernah diam-diam memakai catatan (nama penerima) baris lain.
 */
export function orderEditorAddLine(
  lines: readonly OrderEditorLine[],
  variation: { variationId: string; variationName: string; unitPrice?: number },
  qty: number
): OrderEditorLine[] {
  if (!(qty > 0)) return [...lines];
  const target = lines.find(
    (l) => l.variationId === variation.variationId && !normalizeLineNote(l.note)
  );
  if (target) {
    return lines.map((l) => (l.uid === target.uid ? { ...l, qty: l.qty + qty } : l));
  }
  return [
    ...lines,
    {
      uid: newLineUid(),
      variationId: variation.variationId,
      variationName: variation.variationName,
      unitPrice: variation.unitPrice,
      qty
    }
  ];
}

/** Ubah jumlah SATU baris. */
export function orderEditorSetQty(
  lines: readonly OrderEditorLine[],
  uid: string,
  qty: number
): OrderEditorLine[] {
  return lines.map((l) => (l.uid === uid ? { ...l, qty } : l));
}

/**
 * Ubah catatan SATU baris saat mengetik: hanya dipotong ke batas panjang, TANPA
 * trim (cermin `updateNoteInCarts` — trim per ketukan membuat spasi mustahil
 * diketik); trim akhir terjadi di `orderEditPayloadLines`.
 */
export function orderEditorSetNote(
  lines: readonly OrderEditorLine[],
  uid: string,
  note: string
): OrderEditorLine[] {
  const capped = note.slice(0, MAX_LINE_NOTE_LENGTH);
  return lines.map((l) => (l.uid === uid ? { ...l, note: capped } : l));
}

/** Hapus SATU baris (kembarannya yang bercatatan lain tetap). */
export function orderEditorRemoveLine(
  lines: readonly OrderEditorLine[],
  uid: string
): OrderEditorLine[] {
  return lines.filter((l) => l.uid !== uid);
}

/** Satu baris muatan Edit Pesanan SEBELUM harganya dipasang (`orderEditPricedLine`). */
export type OrderEditPayloadLine = {
  variationId: string;
  variationName: string;
  qty: number;
  note: string | null;
};

/**
 * Muatan Simpan: trim, qty dibulatkan (≤ 0 dibuang), catatan dalam bentuk
 * simpanan server, lalu baris SEIDENTITAS (varian + catatan) dilebur — qty
 * dijumlah. Baris bercatatan beda TETAP terpisah: RPC menyisipkan satu baris
 * `order_items` per elemen muatan, jadi di peleburan inilah catatan kembar dulu
 * hilang (kuncinya varian saja, catatan diambil yang terakhir).
 */
export function orderEditPayloadLines(lines: readonly CartLine[]): OrderEditPayloadLine[] {
  const byIdentity = new Map<string, OrderEditPayloadLine>();
  for (const line of lines) {
    const variationId = line.variationId.trim();
    const variationName = line.variationName.trim();
    const qty = Math.max(Math.round(line.qty), 0);
    const note = normalizeLineNote(line.note) ?? null;
    if (!variationName || qty <= 0) continue;
    const identity = orderEditLineIdentity({ variationId, variationName, note });
    const prev = byIdentity.get(identity);
    if (prev) {
      prev.qty += qty;
      continue;
    }
    byIdentity.set(identity, { variationId, variationName, qty, note });
  }
  return [...byIdentity.values()];
}

/** Harga satu baris dari `sellableSelectionPrice` (pricing.ts) — harga TERKINI. */
export type OrderEditResolvedPrice = {
  price: number | undefined;
  promo: ProductPromo | null;
  tier: PriceTier;
  tier2: PriceTier;
  cuts: readonly PriceCut[];
  normalPrice: number | undefined;
};

/** Katalog harga Edit Pesanan — bentuk resolver checkout (`buildCheckoutLineResolver`). */
export type OrderEditPriceCatalog = {
  variations: readonly ProductVariation[];
  priceNormal: number | undefined;
  /** Sumbu harga (mig 7680): null = tak diketahui (fail-closed); absen = aturan lama. */
  priceAxis?: boolean | null;
  /** Warisan slot varian ini (RPC `preorder_inherited_pricing`); null/absen = tak ada. */
  inherited?: Parameters<typeof resolveSelectionPrice>[1]["inherited"];
};

/**
 * Harga satu varian di Edit Pesanan pembeli — SATU rumus untuk angka yang
 * dipajang sheet, baris yang ditambahkan, dan muatan Simpan, cermin resolver
 * checkout: `resolveSelectionPrice` + `sellableSelectionPrice` dengan sumbu
 * harga & warisan slot. `price` undefined = harga menyusul — BUKAN harga
 * perwakilan/produk (bughunt D15-22, kelas #2095 "dua angka berbeda"). Tanpa
 * sumbu, baris bersumbu yang dihargai checkout lewat warisan slot dulu
 * tertulis ulang harga menyusul di setiap Simpan (D15-33).
 */
export function orderEditSelectionPrice(
  variation: ProductVariation,
  catalog: OrderEditPriceCatalog,
  group: CustomerGroup,
  promos: readonly ProductPromo[] | null
): OrderEditResolvedPrice & { priceExtra: number } {
  return sellableSelectionPrice(
    resolveSelectionPrice(
      variation,
      {
        variations: [...catalog.variations],
        priceNormal: catalog.priceNormal,
        ...(catalog.priceAxis !== undefined ? { priceAxis: catalog.priceAxis } : {}),
        ...(catalog.inherited !== undefined ? { inherited: catalog.inherited } : {})
      },
      group,
      promos
    )
  );
}

/**
 * Perlukah warisan slot untuk varian ini? Gerbang resolver checkout: baris
 * tanpa harga sendiri pada produk yang sumbunya bukan `false`, dan ber-opsi.
 */
export function orderEditNeedsInherited(
  variation: Pick<ProductVariation, "price_normal" | "optionIds">,
  priceAxis: boolean | null | undefined
): boolean {
  return variation.price_normal === undefined && priceAxis !== false && (variation.optionIds ?? []).length > 0;
}

/**
 * Muatan satu baris Edit Pesanan untuk RPC `update_open_preorder_order_items`:
 * harga PRA-voucher (`unitPrice`) + jejak potongan (mig 5990) + harga umum &
 * tier (mig 6460). Kunci harga umum & tier adalah bahan mesin voucher — isinya
 * sama dengan `voucherRpcLinePayload` checkout ber-voucher, supaya server
 * menghitung harga akhir pesanan yang diedit dengan rumus checkout; RPC
 * mengabaikannya untuk pesanan tanpa voucher.
 *
 * Harga yang belum bisa dipastikan (`price` undefined, alur "harga menyusul")
 * dikirim `null` — kuncinya TETAP ada: tanpa kunci, RPC memakai harga lama
 * baris itu, dan jalur voucher menolak edit karena harganya dianggap belum
 * termuat.
 */
export function orderEditPricedLine<L extends object>(
  line: L,
  resolved: OrderEditResolvedPrice & { priceExtra?: number }
): L & Record<string, unknown> {
  const trace = discountTracePayload(resolved.cuts);
  return {
    ...line,
    unitPrice: resolved.price === undefined ? null : resolved.price,
    // mig 8090 (bughunt D15-33): tambahan yang TERMASUK di unitPrice — server
    // mencocokkannya; hanya untuk baris berharga (absen = trigger).
    ...orderLinePriceExtraKey({ unitPrice: resolved.price ?? null, priceExtra: resolved.priceExtra }),
    normalPrice: resolved.normalPrice === undefined ? null : resolved.normalPrice,
    tierMode: resolved.tier?.mode ?? null,
    tierValue: resolved.tier?.value ?? null,
    tier2Mode: resolved.tier2?.mode ?? null,
    tier2Value: resolved.tier2?.value ?? null,
    promoCampaignId: resolved.promo?.campaignId ?? null,
    promoCampaignName: resolved.promo?.name ?? null,
    promoDiscountMode: trace.promo_discount_mode,
    promoDiscountValue: trace.promo_discount_value,
    levelDiscountMode: trace.level_discount_mode,
    levelDiscountValue: trace.level_discount_value
  };
}

/**
 * Varian di muatan Edit Pesanan yang akan diberi harga UMUM oleh server tanpa
 * ada yang tahu (bughunt D13-3): baris TANPA kunci `unitPrice` (konteks harga
 * gagal dimuat, atau varian tak terbaca di konteks) yang juga TANPA snapshot
 * harga lama di pesanan itu. Untuk baris seperti itu `update_open_preorder_order_items`
 * jatuh ke `price_normal` varian — tanpa diskon level, aturan merek, maupun
 * promo. Baris yang PUNYA snapshot aman: server mempertahankan harga lamanya.
 *
 * `previous` = baris pesanan yang tersimpan (`LocalOrder.lines`, semua baris —
 * cermin `prev_item_prices` server: setiap `order_items` pesanan itu yang
 * `unit_price`-nya tak NULL, dicocokkan per NAMA varian). Pemanggil menolak
 * Simpan bila hasilnya tak kosong; mengirimnya berarti menagih pembeli harga
 * penuh diam-diam.
 */
export function orderEditUnpricedNewVariants(
  payload: ReadonlyArray<Record<string, unknown>>,
  previous: ReadonlyArray<{ variationName: string; unitPrice?: number | null }>
): string[] {
  const snapshot = new Set(
    previous
      .filter((l) => l.unitPrice !== undefined && l.unitPrice !== null)
      .map((l) => l.variationName)
  );
  const out: string[] = [];
  for (const line of payload) {
    if ("unitPrice" in line) continue;
    const name = String(line.variationName ?? "");
    if (!snapshot.has(name) && !out.includes(name)) out.push(name);
  }
  return out;
}

/**
 * Nama voucher pada baris HIDUP pesanan (`null` = tak ber-voucher). Baris yang
 * sudah dibatalkan tidak dihitung — cermin gerbang server yang menyaring
 * `keep_status <> 'cancelled'` (audit 2026-09-10).
 */
export function liveVoucherName(items: readonly OrderEditItemRow[]): string | null {
  for (const item of items) {
    if (item.keep_status === "cancelled") continue;
    const name = (item.voucher_name ?? "").trim();
    if (name) return name;
  }
  return null;
}

/**
 * Baris yang MENGUNCI tulis-ulang pesanan (gerbang `HARGA_MANUAL`, mig 7060 §4):
 * baris yang akan ditulis ulang (keep NULL) dan berharga manual — Harga Jual
 * baris (`manual_price_normal`) atau diskon manual dari keranjang admin.
 * DELETE+INSERT Edit Pesanan / Edit Variasi tak membawa kolom itu, jadi harga
 * yang disepakati admin akan kembali ke harga katalog diam-diam. Produk Non Web
 * juga selalu ber-Harga baris, tapi pesanannya tak pernah lewat jalur tulis-
 * ulang (bukan OPEN/CLOSED PO) — cermin persis kondisi SQL-nya.
 */
export function manualPriceLocksRewrite(row: {
  keepStatus: string | null;
  manualPriceNormal: number | null | undefined;
  manualDiscountMode: string | null | undefined;
}): boolean {
  return (
    row.keepStatus == null &&
    (row.manualDiscountMode != null || row.manualPriceNormal != null)
  );
}

/** Pesanan (baris mentah `order_items`) yang terkunci gerbang harga manual. */
export function orderHasManualPrice(
  items: ReadonlyArray<{
    keep_status?: string | null;
    manual_price_normal?: number | string | null;
    manual_discount_mode?: string | null;
  }>
): boolean {
  return items.some((item) =>
    manualPriceLocksRewrite({
      keepStatus: item.keep_status ?? null,
      manualPriceNormal:
        item.manual_price_normal == null || item.manual_price_normal === ""
          ? null
          : Number(item.manual_price_normal),
      manualDiscountMode: item.manual_discount_mode ?? null
    })
  );
}

/** Teks pengganti tombol Edit Pesanan pembeli — SAMA dengan pesan server mig 7060. */
export const MANUAL_PRICE_EDIT_LOCK_TEXT =
  "Harga pesanan ini diatur admin — hubungi admin untuk mengubahnya.";

/**
 * Teks pengganti tombol Edit Pesanan pembeli untuk pesanan yang memuat baris
 * BUNDEL hidup (mig 8040 §7c, gerbang `detail = 'BUNDEL'`): tulis-ulang Edit
 * Pesanan membuang harga & jejak bundel, jadi pembeli memakai Batalkan Bundel.
 */
export const BUNDLE_EDIT_LOCK_TEXT =
  "Pesanan ini memuat bundling — ubah lewat Batalkan Bundling";

/**
 * Pengganti `BUNDLE_EDIT_LOCK_TEXT` saat tombol Batalkan Bundel TIDAK tersedia
 * (PO ditutup, sudah ternota/dibayar, voucher dipakai bersama barang lain, …):
 * menyuruh pembeli memakai tombol yang tak ada = jalan buntu.
 */
export const BUNDLE_EDIT_LOCK_ADMIN_TEXT =
  "Pesanan ini memuat bundling — hubungi admin untuk mengubahnya.";

/**
 * Teks pengganti tombol Edit Pesanan pada pesanan berbundel. "ubah lewat
 * Batalkan Bundel" HANYA bila setiap pembelian bundel di pesanan itu memang
 * menampilkan tombol Batalkan Bundel (`bundleCancelAvailable` per pembelian);
 * selain itu — termasuk bila tak satu pun pembeliannya terbaca — hubungi admin.
 */
export function bundleEditLockText(bundleCancelAvailable: readonly boolean[]): string {
  return bundleCancelAvailable.length > 0 && bundleCancelAvailable.every(Boolean)
    ? BUNDLE_EDIT_LOCK_TEXT
    : BUNDLE_EDIT_LOCK_ADMIN_TEXT;
}

/**
 * Pesanan (baris mentah `order_items`) yang memuat baris bundel HIDUP — cermin
 * kondisi SQL gerbang BUNDEL (`order_bundle_id is not null` dan bukan batal).
 */
export function orderHasLiveBundle(
  items: ReadonlyArray<{
    keep_status?: string | null;
    order_bundle_id?: string | null;
  }>
): boolean {
  return items.some(
    (item) => Boolean(item.order_bundle_id) && item.keep_status !== "cancelled"
  );
}

/**
 * Tombol "Edit Pesanan" pembeli: produk masih OPEN PO dan pesanan masih
 * Menunggu/Diproses (cermin gerbang RPC). Pesanan ber-voucher TIDAK lagi
 * dikecualikan (mig 6460) — server yang menilai vouchernya saat disimpan.
 * Pesanan yang harganya diatur admin (mig 7060, `hasManualPrice`) DIKUNCI —
 * cermin gerbang `HARGA_MANUAL`; tanpanya pembeli membuka editor yang pasti
 * ditolak saat disimpan. Begitu pula pesanan yang memuat baris BUNDEL
 * (mig 8040, `hasBundle`) — cermin gerbang `BUNDEL`. Pesanan BERHADIAH tak lagi
 * dikunci (mig 8850, H9 dicabut): hadiahnya ikut menyesuaikan. Pesanan yang
 * isinya hanya baris hadiah (`editLines` kosong) tak punya apa pun untuk diedit.
 */
export function canEditOrder(order: {
  productStatus?: string;
  status: string;
  hasManualPrice?: boolean;
  hasBundle?: boolean;
  editLines?: readonly unknown[];
}): boolean {
  return (
    order.productStatus === "OPEN PO" &&
    (order.status === "Menunggu" || order.status === "Diproses") &&
    !order.hasManualPrice &&
    !order.hasBundle &&
    (order.editLines === undefined || order.editLines.length > 0)
  );
}

/**
 * Pesan galat Simpan Edit Pesanan untuk pembeli.
 *
 * Penolakan jalur voucher (mig 6460, `details = 'VOUCHER_EDIT:<sebab>'`) sudah
 * ditulis server dalam bahasa pembeli lengkap dengan jalan keluarnya ("muat
 * ulang halaman" / "hubungi admin") — ditampilkan apa adanya. Selebihnya diberi
 * awalan yang menyebut apa yang gagal. Pesan lama "Jalankan migration 011
 * terlebih dahulu" adalah sisa masa pengembangan dan tak pernah benar bagi
 * pembeli.
 */
export function orderEditErrorMessage(
  error: { message?: string | null; details?: string | null } | null | undefined
): string {
  const message = (error?.message ?? "").trim();
  const details = (error?.details ?? "").trim();
  if (message && details.startsWith("VOUCHER_EDIT:")) return message;
  // Gerbang harga manual (mig 7060): kalimatnya sudah untuk pembeli.
  if (message && details === "HARGA_MANUAL") return message;
  // Gerbang bundel (mig 8040): jalan keluarnya tombol Batalkan Bundel.
  if (details === "BUNDEL") return BUNDLE_EDIT_LOCK_TEXT;
  return message
    ? `Gagal menyimpan perubahan pesanan: ${message}`
    : "Gagal menyimpan perubahan pesanan. Coba lagi.";
}
